import { useEffect, useLayoutEffect } from 'react';
import {
  buildLucideSvgElement,
  localLucideIcons,
  resolveIconRegistry
} from '../utils/lucideHelpers.js';
import { resolveStaticUrl } from '../utils/staticAssets.js';

export const usePageStyles = (hrefs = []) => {
  useLayoutEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    hrefs.filter(Boolean).forEach((href) => {
      const existing = document.querySelector(
        `link[data-dynamic-style="${href}"], link[href="${href}"]`
      );
      if (existing) {
        return;
      }
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.dataset.dynamicStyle = href;
      link.fetchPriority = link.fetchPriority || 'high';
      document.head.appendChild(link);
    });
  }, [hrefs.join('|')]);
};

export const useLucideIcons = (deps = []) => {
  useEffect(() => {
    let retryId = null;
    let cancelled = false;
    let importAttempted = false;
    let cdnScriptInjected = false;

    const renderIcons = (lucideInstance) => {
      const registry = resolveIconRegistry(lucideInstance);
      if (!registry) {
        return false;
      }

      // Render SVG markup inside existing <i data-lucide> tags.
      // Avoid lucide.createIcons because it replaces nodes and breaks React's DOM ownership.
      const elements = document.querySelectorAll('[data-lucide]');
      elements.forEach((element) => {
        const name = element.getAttribute('data-lucide');
        const sizeAttr = element.getAttribute('data-size') || element.getAttribute('data-lucide-size');
        const width = sizeAttr || element.getAttribute('width') || undefined;
        const height = sizeAttr || element.getAttribute('height') || undefined;
        const stroke = element.getAttribute('data-stroke-width') || element.getAttribute('stroke-width') || undefined;
        const svgElement = buildLucideSvgElement(registry, name, {
          width,
          height,
          strokeWidth: stroke,
          className: element.getAttribute('class') || undefined,
          ariaHidden: element.getAttribute('aria-hidden') || undefined
        });
        if (!svgElement) {
          element.innerHTML = '';
          return;
        }
        element.replaceChildren(svgElement);
      });
      return true;
    };

    const ensureIcons = async () => {
      if (cancelled || typeof window === 'undefined' || typeof document === 'undefined') {
        return;
      }
      let lucideInstance = window.lucide || (localLucideIcons ? { icons: localLucideIcons } : null);
      if ((!lucideInstance || !lucideInstance.icons) && !importAttempted) {
        importAttempted = true;
        try {
          const mod = await import('lucide');
          if (!cancelled && mod) {
            window.lucide = mod;
            lucideInstance = mod;
          }
        } catch (e) {
          // ignore import errors, will try script fallback
        }
      }

      // Usa primero el bundle local (mismo que el index) antes de hacer fallback a CDN
      const injectLucideScript = (src, marker) => {
        const existing = document.querySelector(`script[data-${marker}-lucide]`);
        if (existing) {
          return true;
        }
        const script = document.createElement('script');
        script.src = src;
        script.defer = true;
        script.dataset[`${marker}Lucide`] = 'true';
        script.onload = () => {
          if (globalThis.lucide && !window.lucide) {
            window.lucide = globalThis.lucide;
          }
          if (!cancelled) {
            ensureIcons();
          }
        };
        document.head.appendChild(script);
        return true;
      };

      // Si no hay íconos (por ejemplo, falla el import dinámico), usa CDN como último recurso
      if ((!lucideInstance || !lucideInstance.icons) && !cdnScriptInjected) {
        cdnScriptInjected = injectLucideScript(
          'https://unpkg.com/lucide@latest/dist/umd/lucide.js',
          'inline'
        );
      }

      if (renderIcons(lucideInstance)) {
        return;
      }
      retryId = window.setTimeout(ensureIcons, 150);
    };

    // Render immediately after React commits to avoid Lucide replacing nodes that React owns.
    ensureIcons();

    return () => {
      cancelled = true;
      if (retryId) {
        window.clearTimeout(retryId);
      }
    };
  }, deps);
};
